import { useCallback, useEffect, useState } from 'react';
import { Card, Collapse, Stack } from 'react-bootstrap';
import { faDownload, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

import IconButton from '@components/Button/IconButton';
import Placeholder from '@components/Placeholder/Placeholder';
import { fetchRequisitionMetrics, RequisitionMetrics } from '@store/actions';
import { UI_BUTTON_VAR, IS_NATERA } from '@constants/constants';

import { OrderStatusInfo, RequisitionTable } from './components';
import './Requisitions.scss';

const statusValueClasses = IS_NATERA ? 'order-status--nat' : 'order-status--myo';
const statusTitleClasses = IS_NATERA ? 'fw-medium text-secondary' : 'fw-bold';

export const RequisitionsPage = () => {
  const [metrics, setMetrics] = useState<RequisitionMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [showPatientName, setShowPatientName] = useState(true);
  const [showOrderStatusInfo, setShowOrderStatusInfo] = useState(false);
  const [exportCsvTrigger, setExportCsvTrigger] = useState(0);

  const loadMetrics = useCallback(() => {
    setMetricsLoading(true);
    setMetricsError(null);
    fetchRequisitionMetrics()
      .then(setMetrics)
      .catch(err => setMetricsError(err?.toString() ?? 'Server Error'))
      .finally(() => setMetricsLoading(false));
  }, []);

  const orderStatus: Array<{ title: string; value: number }> = metrics
    ? [
        { title: 'Total Orders', value: metrics.total },
        { title: 'Outstanding Orders', value: metrics.outstanding },
        { title: 'Reported Orders', value: metrics.reported },
      ]
    : [];

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  return (
    <Stack gap={4} className='layout-single-col-py'>
      <section className='d-flex flex-column rounded-1 bg-white box-shadow-50 order-report-section'>
        <h1 className='page-title'>Your orders and reports</h1>

        <p className='mb-0 mt-md-1 text-secondary page-subtitle'>
          View and search orders to download reports and access patient information
        </p>

        <div className='p-0'>
          <div className='d-flex flex-column flex-sm-row mt-3 mt-md-4 justify-content-center order-cards'>
            {orderStatus.map(status => (
              <Card key={status.title} className='order-status border-1 border-primary-subtle'>
                <Card.Body className='py-2 py-md-3'>
                  {metricsLoading ? (
                    <div className='d-flex flex-column gap-3 order-status--title'>
                      <Placeholder size='xl' />
                      <Placeholder />
                    </div>
                  ) : (
                    <>
                      <h2 className={`order-status--value ${metricsError ? 'error' : ''} ${statusValueClasses}`}>
                        {metricsError ? '...' : status.value}
                      </h2>
                      <p className={`order-status--title ${statusTitleClasses}`}>{status.title}</p>
                    </>
                  )}
                </Card.Body>
              </Card>
            ))}
          </div>
        </div>

        <p className='mb-0 mt-3 mt-md-4 fs-xs text-secondary text-center'>
          Outstanding and Reported counts do not include canceled, failed, or denied orders. Their sum is not included
          in the Total count.
        </p>
      </section>

      <div className='d-flex flex-column flex-sm-row gap-3' aria-label='Order management tools'>
        <IconButton
          active={!showPatientName}
          aria-description='Toggle for patient name privacy'
          size='sm'
          startIcon={showPatientName ? faEye : faEyeSlash}
          variant={UI_BUTTON_VAR}
          onClick={() => setShowPatientName(showPatientName => !showPatientName)}
        >
          Patient name
        </IconButton>
        {/* Commenting out the Order Statuses button for now per sc-35056/update-statuses-on-the-orders-and-reports-page */}
        {/* <IconButton
          variant={UI_BUTTON_VAR}
          active={showOrderStatusInfo}
          startIcon={faCircleInfo}
          size='sm'
          onClick={() => setShowOrderStatusInfo(showOrderStatusInfo => !showOrderStatusInfo)}
          aria-label='Toggle order status information'
        >
          Order Statuses
        </IconButton> */}
        <IconButton
          aria-label='Export orders as CSV'
          size='sm'
          startIcon={faDownload}
          variant={UI_BUTTON_VAR}
          onClick={() => setExportCsvTrigger(prev => prev + 1)}
        >
          Export CSV
        </IconButton>
      </div>

      <Collapse in={showOrderStatusInfo} className='order-status-collapse'>
        <div>
          <OrderStatusInfo />
        </div>
      </Collapse>

      <RequisitionTable exportCsvTrigger={exportCsvTrigger} showPatientName={showPatientName} />
    </Stack>
  );
};
